'use client';

import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { MessageCircle } from 'lucide-react';
import { useChatbot } from './chatbot-provider';
import { NavigationToast } from './navigation-toast';
import { CHATBOT_OPEN_EVENT, type ChatbotOpenEventDetail } from './chatbot-open-event';

const ChatbotPanel = dynamic(
  () => import('./chatbot-panel').then(m => ({ default: m.ChatbotPanel })),
  {
    ssr: false,
    loading: () => (
      <div data-page-overlay="chatbot" className="fixed bottom-4 right-4 z-50 flex flex-col w-[22rem] sm:w-96 h-[32rem] rounded-box shadow-2xl overflow-hidden bg-base-100 border border-base-300 items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    ),
  },
);

const isChatbotOpenEventDetail = (value: unknown): value is ChatbotOpenEventDetail => {
  if (value === null || typeof value !== 'object') return false;
  if (!('initialMessage' in value)) return true;
  const { initialMessage } = value;
  return initialMessage === undefined || typeof initialMessage === 'string';
};

export const ChatbotBubble: React.FC = () => {
  const { isOpen, open, navigationToast, dismissNavigationToast } = useChatbot();

  // Listen for cross-boundary requests to open the chatbot (e.g. from
  // <ChatbotInlineInput /> rendered inside the page body, which sits outside
  // the chatbot provider's React subtree).
  useEffect(() => {
    const handler = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const { detail } = event;
      if (!isChatbotOpenEventDetail(detail)) return;
      open(detail.initialMessage);
    };
    window.addEventListener(CHATBOT_OPEN_EVENT, handler);
    return () => window.removeEventListener(CHATBOT_OPEN_EVENT, handler);
  }, [open]);

  return (
    <>
      {isOpen ? (
        <ChatbotPanel />
      ) : navigationToast ? (
        <NavigationToast
          label={navigationToast.label}
          url={navigationToast.url}
          onDismiss={dismissNavigationToast}
          onNotUseful={() => {
            dismissNavigationToast();
            open();
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => open()}
          data-page-overlay="chatbot"
          className="fixed bottom-4 right-4 z-50 btn btn-circle btn-primary btn-lg shadow-lg shadow-primary/30 ring-1 ring-primary/20 transition-transform duration-200 hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100"
        >
          <MessageCircle className="size-6" />
          <span className="absolute -top-0.5 -right-0.5 size-3 rounded-full bg-success ring-2 ring-base-100" />
          <span className="sr-only">Open chat</span>
        </button>
      )}
    </>
  );
};
